import { useIntl } from 'react-intl';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';
import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import { BODY_BUILDER_OPTIONS, IMPLEMENT_OPTIONS, VEHICLE_TYPE_OPTIONS } from '../constants';
import { toSelectOptions } from '../../../../../../utils/aeg';
import { AegFormSectionProps, AegBodyBuilderNameEnum, AegImplementOrBodyBuilderEnum, type AegVehicleTypeEnum } from '../../../../../../models/newAEG';

const VehicleCustomerSection = ({ values, errors, requiredFields, onChange }: AegFormSectionProps) => {
    const intl = useIntl();
    const requiredMessage = intl.formatMessage({ id: 'intl-msg:support.custom.fieldRequired' });
    const selectPlaceholder = intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.select' });
    const noItemMessage = intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.select.noItemFound' });

    return (
        <section className='border rounded padding-15'>
            <h3 className='display-flex align-items-center gap-x-2 margin-top-0 margin-bottom-15 text-size-16 text-bold'>
                <span className='rounded-circle bg-primary text-color-white text-size-12 width-25 height-25 inline-flex items-center justify-center shrink-0'>
                    3
                </span>
                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.section.vehicleCustomer' })}
            </h3>
            <div className='grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3'>
                <div className='form-group margin-bottom-0 col-span-full lg:col-span-2'>
                    <label htmlFor='aeg-occurrence' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.occurrence' })}{' '}
                        {requiredFields.includes('occurrence') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-occurrence'
                        options={[] as SelectOption[]}
                        value={values.occurrence ? [values.occurrence] : []}
                        onChange={option => onChange('occurrence', option?.id ?? '')}
                        placeholder={selectPlaceholder}
                        noItemMessage={noItemMessage}
                    />
                    {errors.occurrence && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-chassis' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.chassis' })}{' '}
                        {requiredFields.includes('chassis') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-chassis'
                        type='text'
                        className='form-control'
                        maxLength={17}
                        value={values.chassis}
                        onChange={event => onChange('chassis', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.chassis' })}
                    />
                    {errors.chassis && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-license-plate' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.licensePlate' })}{' '}
                        {requiredFields.includes('licensePlate') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-license-plate'
                        type='text'
                        className='form-control'
                        value={values.licensePlate}
                        onChange={event => onChange('licensePlate', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.licensePlate',
                        })}
                    />
                    {errors.licensePlate && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-vehicle-type' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.vehicleType' })}{' '}
                        {requiredFields.includes('vehicleType') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-vehicle-type'
                        options={toSelectOptions(VEHICLE_TYPE_OPTIONS, intl)}
                        value={values.vehicleType ? [values.vehicleType] : []}
                        onChange={option => onChange('vehicleType', (option?.id ?? '') as AegVehicleTypeEnum | '')}
                        placeholder={selectPlaceholder}
                    />
                    {errors.vehicleType && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-customer' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.customer' })}{' '}
                        {requiredFields.includes('customer') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-customer'
                        type='text'
                        className='form-control'
                        value={values.customer}
                        onChange={event => onChange('customer', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.customer' })}
                    />
                    {errors.customer && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-model' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.model' })}{' '}
                        {requiredFields.includes('model') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-model'
                        type='text'
                        className='form-control'
                        value={values.model}
                        onChange={event => onChange('model', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.model' })}
                        disabled
                    />
                    {errors.model && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-implement' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.implement' })}{' '}
                        {requiredFields.includes('implement') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-implement'
                        options={toSelectOptions(IMPLEMENT_OPTIONS, intl)}
                        value={values.implement ? [values.implement] : []}
                        onChange={option =>
                            onChange('implement', (option?.id ?? '') as AegImplementOrBodyBuilderEnum | '')
                        }
                        placeholder={selectPlaceholder}
                        noItemMessage={noItemMessage}
                        useFilter
                    />
                    {errors.implement && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                {values.implement === AegImplementOrBodyBuilderEnum.OTHERS && (
                    <div className='form-group margin-bottom-0 border-l-4 border-color-primary padding-left-10'>
                        <label htmlFor='aeg-implement-other' className='text-size-12 text-medium text-color-dark'>
                            {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.implementOther' })}{' '}
                            {requiredFields.includes('implementOther') && <span className='text-color-danger'>*</span>}
                        </label>
                        <input
                            id='aeg-implement-other'
                            type='text'
                            className='form-control'
                            value={values.implementOther}
                            onChange={event => onChange('implementOther', event.target.value)}
                        />
                        {errors.implementOther && (
                            <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                        )}
                    </div>
                )}
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-body-builder' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.bodyBuilder' })}{' '}
                        {requiredFields.includes('bodyBuilder') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-body-builder'
                        options={toSelectOptions(BODY_BUILDER_OPTIONS, intl)}
                        value={values.bodyBuilder ? [values.bodyBuilder] : []}
                        onChange={option => onChange('bodyBuilder', (option?.id ?? '') as AegBodyBuilderNameEnum | '')}
                        placeholder={selectPlaceholder}
                        noItemMessage={noItemMessage}
                        useFilter
                    />
                    {errors.bodyBuilder && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                {values.bodyBuilder === AegBodyBuilderNameEnum.OTHERS && (
                    <div className='form-group margin-bottom-0 border-l-4 border-color-primary padding-left-10'>
                        <label htmlFor='aeg-body-builder-other' className='text-size-12 text-medium text-color-dark'>
                            {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.bodyBuilderOther' })}{' '}
                            {requiredFields.includes('bodyBuilderOther') && (
                                <span className='text-color-danger'>*</span>
                            )}
                        </label>
                        <input
                            id='aeg-body-builder-other'
                            type='text'
                            className='form-control'
                            value={values.bodyBuilderOther}
                            onChange={event => onChange('bodyBuilderOther', event.target.value)}
                        />
                        {errors.bodyBuilderOther && (
                            <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                        )}
                    </div>
                )}
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-cargo' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.cargo' })}{' '}
                        {requiredFields.includes('cargo') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-cargo'
                        type='text'
                        className='form-control'
                        value={values.cargo}
                        onChange={event => onChange('cargo', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.cargo' })}
                    />
                    {errors.cargo && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-odometer' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.odometer' })}{' '}
                        {requiredFields.includes('odometer') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-odometer'
                        type='text'
                        className='form-control'
                        value={values.odometer}
                        onChange={event => onChange('odometer', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.odometer' })}
                    />
                    {errors.odometer && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-hour-meter' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.hourMeter' })}{' '}
                        {requiredFields.includes('hourMeter') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-hour-meter'
                        type='text'
                        className='form-control'
                        value={values.hourMeter}
                        onChange={event => onChange('hourMeter', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.hourMeter',
                        })}
                    />
                    {errors.hourMeter && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-repair-date' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.repairDate' })}{' '}
                        {requiredFields.includes('repairDate') && <span className='text-color-danger'>*</span>}
                    </label>
                    <DatePicker
                        locale='pt-BR'
                        dateFormat='DD/MM/YYYY'
                        timeFormat={false}
                        mandatory={false}
                        onChange={(date, isValid) =>
                            onChange('repairDate', isValid && typeof date !== 'string' ? date.format('YYYY-MM-DD') : '')
                        }
                        inputProps={{ id: 'aeg-repair-date' }}
                        className='margin-0'
                    />
                    {errors.repairDate && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-last-purchase' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.lastPurchase' })}{' '}
                        {requiredFields.includes('lastPurchase') && <span className='text-color-danger'>*</span>}
                    </label>
                    <DatePicker
                        locale='pt-BR'
                        dateFormat='DD/MM/YYYY'
                        timeFormat={false}
                        mandatory={false}
                        onChange={(date, isValid) =>
                            onChange(
                                'lastPurchase',
                                isValid && typeof date !== 'string' ? date.format('YYYY-MM-DD') : ''
                            )
                        }
                        inputProps={{ id: 'aeg-last-purchase' }}
                        className='margin-0'
                    />
                    {errors.lastPurchase && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
            </div>
        </section>
    );
};

export default VehicleCustomerSection;
